import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CacheTtlMs, CachedDataService } from './cached-data.service';

// models
import { ICompany } from '../models/company.model';
import { IEmployment, IEmploymentLoading } from '../models/employment.model';
import { ISkill } from '../models/skill.model';
import { IUrlMetadata } from '../models/user-activity-metadata.model';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable()
export class EmploymentExperienceService {
  private controller = '/api/Employment/';
  private readonly EMPLOYMENT_CACHE_KEY = 'employmentExperience';

  constructor(private http: HttpClient, private cachedDataService: CachedDataService) {}

  getCompaniesFromUrl(url: string): Observable<ICompany> {
    const endpoint = this.controller + `GetCompanyFromUrl/`;
    return this.http.get<ICompany>(endpoint, { params: { url } });
  }

  getEmploymentExperience(): Observable<IEmploymentLoading[]> {
    return this.cachedDataService.getOrSet<IEmploymentLoading[]>(
      this.EMPLOYMENT_CACHE_KEY,
      () => {
        const endpoint = this.controller + 'GetEmploymentExperience/';
        return this.http.get<IEmploymentLoading[]>(endpoint);
      },
      CacheTtlMs.OneDay
    );
  }

  getEmploymentSkills(goalId: number, employmentId: number, includeAll: boolean): Observable<ISkill[]> {
    const cacheKey = this.getSkillsCacheKey(goalId, employmentId, includeAll);
    return this.cachedDataService.getOrSet<ISkill[]>(
      cacheKey,
      () => {
        const endpoint = this.controller + `GetEmploymentSkills/`;
        return this.http.get<ISkill[]>(endpoint, {
          params: {
            goalId: goalId.toString(),
            employmentId: employmentId.toString(),
            includeAll: includeAll.toString(),
          },
        });
      },
      CacheTtlMs.FiveMinutes
    );
  }

  clearEmploymentSkillsCache(goalId?: number, employmentId?: number, includeAll?: boolean): void {
    if (goalId !== undefined && employmentId !== undefined && includeAll !== undefined) {
      const cacheKey = this.getSkillsCacheKey(goalId, employmentId, includeAll);
      this.cachedDataService.clear(cacheKey);
      return;
    }

    this.cachedDataService.clearByPrefix('employmentSkills.');
  }

  private getSkillsCacheKey(goalId: number, employmentId: number, includeAll: boolean): string {
    return `employmentSkills.${employmentId}.${goalId}.${includeAll}`;
  }

  clearEmploymentExperienceCache(): void {
    this.cachedDataService.clear(this.EMPLOYMENT_CACHE_KEY);
  }

  addEmploymentExperience(employmentExperience: IEmployment): Observable<IEmployment> {
    const endpoint = this.controller + 'AddEmploymentExperience/';
    return this.http.post<IEmployment>(endpoint, employmentExperience, httpOptions);
  }

  updateEmploymentExperience(employmentExperience: IEmployment): Observable<IEmployment> {
    const endpoint = this.controller + 'UpdateEmploymentExperience/';
    return this.http.put<IEmployment>(endpoint, employmentExperience, httpOptions);
  }

  getCompanyMetadata(url: string): Observable<IUrlMetadata> {
    const endpoint = `/api/UserActivities/GetMetadata/`;
    return this.http.get<IUrlMetadata>(endpoint, { params: { url } });
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  deleteEmploymentExperience(employmentExperience: IEmployment): Observable<{}> {
    const endpoint = this.controller + `${employmentExperience.id}/`;
    return this.http.delete(endpoint, httpOptions);
  }

  getIndustryOptions(): Observable<string[]> {
    const endpoint = this.controller + 'GetAllIndustryOptions/';
    return this.http.get<string[]>(endpoint, httpOptions);
  }

  getIndustriesFromGoogleTypes(types: string[]): Observable<string[]> {
    const cacheKey = `googleIndustryMappings.${types.sort().join(',')}`;
    return this.cachedDataService.getOrSet<string[]>(
      cacheKey,
      () => {
        const endpoint = this.controller + 'GetIndustriesFromGoogleTypes/';
        return this.http.post<string[]>(endpoint, types, httpOptions);
      },
      CacheTtlMs.OneDay
    );
  }
}
